// Financial thunks — payments, refunds, Stripe checkout
import { dispatch, store } from '../../store';
import { updateError } from '../user';
import { callApi } from '../../../utils/apiCall';
import extractApiErrorMessage from '../../../utils/extractApiError';
import { getCurrentTreatmentIdV2 } from '../clincheck';
import { leadActions, startLoadingData, updateCompleteStatus } from './slice';
import { sendPaymentLink, summarisePaymentLinkResult, hasAnySuccessfulChannel } from './paymentLinkThunks';

// 'email' | 'whatsapp' | 'both' (DentallyBookingFormPayment / PaymentOption UI value) → contract A/B `channels` array.
const CHANNEL_MAP = { email: ['email'], whatsapp: ['whatsapp'], both: ['email', 'whatsapp'] };

/**
 * Sends the payment link via the server-side command (contract A). `paymentId`
 * is OPTIONAL — pass it only to recover/reuse an existing pending payment
 * (e.g. the payments-tab "Create & send link" recovery action); pass null/
 * undefined for a fresh send, and the command creates the payment and
 * checkout atomically in one transaction.
 *
 * IMPORTANT: this must never be preceded by a legacy POST .../payments/ for
 * a fresh send — Stripe rejecting the checkout would then leave that payment
 * row hidden and pending with no checkout session (Sentry DENTALSEM-CRM-1EQJ,
 * practice 36 lead 192181 had exactly two such orphaned rows). The command's
 * own transaction.atomic() rolls the payment back on a Stripe failure, so
 * skipping the legacy POST is what actually closes that gap — reusing
 * payment_id on 400/409 would silently reproduce it.
 */
export function sendPaymentLinkForTreatmentPayment(practiceId, leadId, treatmentId, paymentId, payment, handleClose) {
    return async () => {
        const channels = CHANNEL_MAP[payment?.paymentLinkChannel] || ['email'];
        const body = {
            ...(paymentId != null ? { payment_id: paymentId } : {}),
            payment_type: payment?.payment_type,
            amount: payment?.amount,
            channels,
            ...(payment?.recipientEmail ? { email: payment.recipientEmail } : {}),
            ...(payment?.recipientPhone ? { phone: payment.recipientPhone } : {}),
            // Contract: the payment-LINK command (this endpoint) takes
            // `appointment_id` — NOT `appointment` (that's only the plain
            // payments create/update endpoint's field name, see
            // leadTreatmentPayment's non-SEND branch below). A wrong id
            // 400s with code `appointment_invalid`; extractApiErrorMessage
            // (used by sendPaymentLink/paymentLinkThunks.js) already
            // surfaces its detail text generically.
            ...(payment?.appointment != null ? { appointment_id: payment.appointment } : {}),
        };
        const result = await sendPaymentLink({
            practiceId,
            leadId,
            treatmentId: getCurrentTreatmentIdV2(treatmentId),
            body,
        });
        dispatch(updateCompleteStatus(false));
        dispatch(startLoadingData(false));
        if (result.ok) {
            // 201 — payment + checkout created atomically (contract A); individual
            // channels may still fail to deliver, but there is no hidden pending
            // row to worry about, so the dialog closes either way.
            const summary = summarisePaymentLinkResult(result.data, {
                email: payment?.recipientEmail,
                phone: payment?.recipientPhone,
            });
            const anyChannelOk = hasAnySuccessfulChannel(result.data);
            dispatch(updateError({ success: anyChannelOk, message: summary || (anyChannelOk ? 'Payment link sent' : 'Payment link could not be delivered') }));
            handleClose('finish', leadId);
        } else {
            // 400 (prerequisites_failed / stripe_checkout_failed) or 409
            // (link_expired) — nothing was written server-side. Keep the dialog
            // open so the user can fix the channel/recipient and retry, instead
            // of closing on a failed send.
            dispatch(updateError({ success: false, message: result.error }));
        }
    };
}

export function leadDetailPayment(handleClose) {
    return async () => {
        try {
            const { newLeadDetail: { payment }, leadDetail } = store.getState().lead;
            const { practiceId } = store.getState().practiceTreatmentList;
            // Take-payment/deposit must post to the ACTIVE treatment (the one the
            // user is viewing/acting on), not lead_treatments[0]. The active
            // treatment is mirrored into multiTreatment.currentTreatmentDataGlobal
            // by ActiveTreatmentProvider; fall back to [0] for single-treatment leads.
            const { currentTreatmentDataGlobal } = store.getState().multiTreatment;
            const activeTreatmentId = currentTreatmentDataGlobal?.treatmentData?.id || leadDetail?.lead_treatments?.[0]?.id;
            if (payment.payment_type !== 'No') {
                dispatch(startLoadingData(true));
                dispatch(leadTreatmentPayment(practiceId, leadDetail?.id, activeTreatmentId, payment, handleClose));
            } else {
                handleClose();
                dispatch(updateCompleteStatus(false));
            }
        } catch (error) {
            dispatch(updateError({ message: JSON.stringify(error.message), success: false }));
            dispatch(updateCompleteStatus(false));
        }
    };
}

export function leadTreatmentPayment(practiceId, leadId, treatmentId, data, handleClose) {
    return async () => {
        try {
            if (data.mode_of_payment === 'SEND') {
                // The UI must not call the legacy payment-record endpoint as a
                // substitute for the command — the command creates the payment
                // and checkout atomically (transaction.atomic(), contract A) and
                // rolls back on a Stripe failure, so there is nothing to create
                // here first. Covers every caller of this thunk: Take-a-Payment
                // (leadDetailPayment), FollowUpV2's non-Dentally booking, and the
                // V1 chains in coreThunks.updatePersonalDetail (~1265) and
                // appointmentThunks.addAppointment (~186).
                dispatch(sendPaymentLinkForTreatmentPayment(practiceId, leadId, treatmentId, null, data, handleClose));
                return;
            }
            const response = await callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/treatments/${getCurrentTreatmentIdV2(treatmentId)}/payments/`, 'post', data);
            if (response?.status === 201) {
                dispatch(updateError({ success: true, message: 'Payment detail added successfully' }));
                dispatch(updateCompleteStatus(false));
                handleClose('finish', leadId);
                dispatch(startLoadingData(false));
            } else {
                dispatch(updateError({ success: false, message: extractApiErrorMessage(response, 'Something went wrong') }));
                dispatch(updateCompleteStatus(false));
            }
        } catch (error) {
            dispatch(leadActions.hasError(error.message));
            dispatch(startLoadingData(false));
        }
    };
}

export function getLeadTreatmentPayment(practiceId, leadId, treatmentId) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/treatments/${treatmentId}/payments/`);
            if (response?.status === 200) {
                dispatch(leadActions.paymentCompleteSlice(response.data.results));
            } else {
                dispatch(updateError({ message: JSON.stringify(response.data), success: false, status: response?.status }));
            }
        } catch (error) {
            dispatch(updateError({ message: JSON.stringify(error.message) || 'Something went wrong', success: false, cause: error instanceof Error ? new Error(error.message) : new Error(String(error?.message ?? 'error')) }));
            dispatch(updateCompleteStatus(false));
        }
    };
}

// "All treatments" payments: fetch each treatment's payments and COMBINE into a
// single list (tagged with lead_treatment metadata) — never /treatments/null/payments/,
// and never per-treatment overwrites of the paymentComplete slice.
export function getLeadAllTreatmentPayments(practiceId, leadId, treatments) {
    return async () => {
        try {
            const list = Array.isArray(treatments) ? treatments : [];
            const settled = await Promise.allSettled(
                list.map((t) =>
                    callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/treatments/${t.id}/payments/`).then((res) =>
                        res?.status === 200
                            ? (res.data.results || []).map((p) => ({ ...p, lead_treatment: t.id, lead_treatment_name: t.treatment }))
                            : []
                    )
                )
            );
            const combined = settled.filter((r) => r.status === 'fulfilled').flatMap((r) => r.value);
            dispatch(leadActions.paymentCompleteSlice(combined));
        } catch (error) {
            dispatch(updateError({ message: JSON.stringify(error.message) || 'Something went wrong', success: false }));
        }
    };
}

export function getLeadPaymentCheckout(practiceId, leadId, treatmentId, paymentId) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/treatments/${treatmentId}/payments/${paymentId}/checkout_sessions/`);
            if (response?.status === 200) {
                dispatch(leadActions.checkoutListingSlice(response.data));
            } else {
                dispatch(updateError({ message: JSON.stringify(response.data), success: false }));
            }
        } catch (error) {
            dispatch(updateError({ message: JSON.stringify(error.message) || 'Something went wrong', success: false }));
            dispatch(updateCompleteStatus(false));
        }
    };
}

export function getLeadRefundRequest(practiceId, leadId, treatmentId, paymentId, checkoutId) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/treatments/${treatmentId}/payments/${paymentId}/checkout_sessions/${checkoutId}/refund_requests/`);
            if (response?.status === 200) {
                dispatch(leadActions.refundListingSlice(response.data.results));
            } else {
                dispatch(updateError({ message: JSON.stringify(response.data), success: false }));
            }
        } catch (error) {
            dispatch(updateError({ message: JSON.stringify(error.message) || 'Something went wrong', success: false }));
            dispatch(updateCompleteStatus(false));
        }
    };
}

export function createRefundRequest(data, practiceId, leadId, treatmentId, paymentId, checkoutId, handleSuccess) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/treatments/${treatmentId}/payments/${paymentId}/checkout_sessions/${checkoutId}/refund_requests/`, 'post', data);
            if (response?.status === 201) {
                handleSuccess({ success: true, message: 'Refund added successfully' });
                dispatch(getLeadRefundRequest(practiceId, leadId, treatmentId, paymentId, checkoutId));
            } else {
                dispatch(updateError({ message: JSON.stringify(response.data), success: false }));
            }
        } catch (error) {
            dispatch(updateError({ message: JSON.stringify(error.message) || 'Something went wrong', success: false }));
            dispatch(updateCompleteStatus(false));
        }
    };
}

export function retryRefundRequest(practiceId, leadId, treatmentId, paymentId, checkoutId, id, handleSuccess) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/treatments/${treatmentId}/payments/${paymentId}/checkout_sessions/${checkoutId}/refund_requests/${id}/retry/`, 'post');
            if (response?.status === 200) {
                handleSuccess({ success: true, message: 'Refund added successfully' });
                dispatch(getLeadRefundRequest(practiceId, leadId, treatmentId, paymentId, checkoutId));
            } else {
                dispatch(updateError({ message: JSON.stringify(response.data), success: false }));
            }
        } catch (error) {
            dispatch(updateError({ message: JSON.stringify(error.message) || 'Something went wrong', success: false }));
            dispatch(updateCompleteStatus(false));
        }
    };
}

export function paymentCheckout(practiceId, lead, treatment, paymentId, handleClose) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/leads/${lead}/treatments/${treatment}/payments/${paymentId}/checkout_sessions/`, 'post', {});
            if (response?.status === 201) {
                dispatch(updateError({ success: true, message: 'Payment link send successfully' }));
                dispatch(updateCompleteStatus(false));
                handleClose('finish', lead);
                dispatch(startLoadingData(false));
            } else {
                dispatch(updateError({ success: false, message: extractApiErrorMessage(response, 'Something went wrong') }));
                dispatch(updateCompleteStatus(false));
                dispatch(startLoadingData(false));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
            dispatch(startLoadingData(false));
        }
    };
}

export function createStripePayment(practiceId, data, handleSuccess) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/checkout_sessions/`, 'post', data);
            if (response?.status === 201) {
                handleSuccess({ success: true, message: 'Stripe payment created successfully', response: response?.data });
            } else {
                dispatch(updateError({ message: JSON.stringify(response?.data), success: false }));
                document.body.classList.remove('points-event');
                handleSuccess({ success: false, message: JSON.stringify(response?.data) });
            }
        } catch (error) {
            dispatch(updateError({ message: JSON.stringify(error.message) || 'Something went wrong', success: false }));
        }
    };
}
